import React, { useState, useEffect, useRef } from "react";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Icon from "@amzn/meridian/icon";
import Divider from "@amzn/meridian/divider";
import Toggle from "@amzn/meridian/toggle";
import Textarea from "@amzn/meridian/textarea";
import Tag from "@amzn/meridian/tag";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import closeMediumTokens from "@amzn/meridian-tokens/base/icon/close-medium";
import userTokens from "@amzn/meridian-tokens/base/icon/user";
import sendSmallTokens from "@amzn/meridian-tokens/base/icon/send-small";
import ReactMarkdown from "react-markdown";
import { useAppSelector } from "../../app/store/hooks";
import { WebSocketService, ChatMessage } from "../../services/websocket";
import { getCurrentWebSocketEndpoint } from "../../configuration/websocket";
import { ThinkingSection } from "./ThinkingSection";
import styles from "./AIChatModal.module.scss";

interface AIChatModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const AIChatModal: React.FC<AIChatModalProps> = ({ isOpen, onClose }) => {
    const { alias } = useAppSelector(state => state.auth.user);
    const { jwtToken } = useAppSelector(state => state.auth);
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            id: "1",
            text: "Hello! I'm your AI Appraiser assistant. How can I help you with property tax assessments today?",
            sender: "ai",
            timestamp: new Date(),
        },
    ]);
    const [inputValue, setInputValue] = useState("");
    const [showContext, setShowContext] = useState(false);
    const [showSkills, setShowSkills] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingMessageIds, setStreamingMessageIds] = useState<Set<string>>(new Set());
    const [isConnected, setIsConnected] = useState(true);
    const [showConnectionAlert, setShowConnectionAlert] = useState(false);
    const wsService = useRef<WebSocketService | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Skills content
    const skillsContent = `Hello! I'm your Property Tax Compliance Management Planning Agent. 
I specialize in monitoring, tracking, and managing all aspects of property tax compliance 
to ensure regulatory adherence and optimize your property tax operations.

**What I Can Help You With:**

### 🔍 Notice Reconciliation
- Identify and resolve value discrepancies between systems
- Reconcile Property Tax Portal and Tango data
- Analyze missing values and data mismatches

### 📊 Compliance Monitoring
- Track compliance deadlines and required actions
- Generate status reports by property, region, or portfolio
- Identify potential risks before they become critical

### 💡 Strategic Insights
- Analyze tax notices and mail content for next steps
- Provide recommendations for Property Tax Managers and Analysts
- Assess financial impacts and prioritize actions

### 🏘️ Property Analysis
- Retrieve property assessments and tax records
- Analyze property-specific risks and opportunities
- Support appeal processes and valuations`;

    // Initialize WebSocket connection when modal opens
    useEffect(() => {
        if (isOpen && !wsService.current) {
            console.log("🚀 AI Chat Modal opened - initializing WebSocket");
            console.log("🔑 JWT Token available:", !!jwtToken);
            const endpoint = getCurrentWebSocketEndpoint();
            wsService.current = new WebSocketService(endpoint, jwtToken);

            wsService.current.onMessage((aiMessage: ChatMessage) => {
                console.log("📨 AI Chat Modal received streaming message:", aiMessage);
                // Set streaming indicator when first chunk arrives
                setIsStreaming(true);

                // Add message ID to streaming set
                setStreamingMessageIds(prev => new Set(prev).add(aiMessage.id));

                setMessages(prev => {
                    // Check if message already exists (for streaming updates)
                    const existingIndex = prev.findIndex(msg => msg.id === aiMessage.id);
                    if (existingIndex >= 0) {
                        // Update existing message with new chunk
                        const updated = [...prev];
                        updated[existingIndex] = {
                            ...aiMessage,
                            thinking: aiMessage.thinking, // Preserve thinking content
                        };

                        return updated;
                    } else {
                        // Add new message
                        return [
                            ...prev,
                            {
                                ...aiMessage,
                                thinking: aiMessage.thinking, // Preserve thinking content
                            },
                        ];
                    }
                });
            });

            wsService.current.onConnect(() => {
                console.log("✅ AI Chat Modal - WebSocket connected");
                setIsConnected(true);
                setShowConnectionAlert(false);
            });

            wsService.current.onDisconnect(() => {
                console.log("🔌 AI Chat Modal - WebSocket disconnected");
                setIsConnected(false);
                setShowConnectionAlert(true);
            });

            wsService.current.onError(error => {
                console.error("❌ AI Chat Modal - WebSocket error:", error);
                setIsConnected(false);
                setShowConnectionAlert(true);
            });

            wsService.current.connect();
        }

        // Cleanup on unmount or when modal closes
        return () => {
            if (!isOpen && wsService.current) {
                console.log("🔌 AI Chat Modal closed - cleaning up WebSocket");
                wsService.current.disconnect();
                wsService.current = null;
            }
        };
    }, [isOpen, jwtToken]);

    const handleSendMessage = () => {
        if (inputValue.trim() && wsService.current) {
            console.log("📤 AI Chat Modal sending message:", inputValue);
            const userMessage: ChatMessage = {
                id: Date.now().toString(),
                text: inputValue,
                sender: "user",
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, userMessage]);
            setIsStreaming(true); // Start streaming indicator
            wsService.current.sendMessage(inputValue, alias || "anonymous");
            setInputValue("");
        } else {
            console.warn("⚠️ Cannot send message - no input or WebSocket not available");
        }
    };

    // Add keyboard event listener for Enter key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSendMessage();
            }
        };

        const textareaElement = textareaRef.current;
        if (textareaElement) {
            textareaElement.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            if (textareaElement) {
                textareaElement.removeEventListener("keydown", handleKeyDown);
            }
        };
    }, [inputValue]);

    const handleReconnect = () => {
        if (wsService.current) {
            console.log("🔄 Attempting to reconnect...");
            wsService.current.disconnect();
            wsService.current.connect();
        }
    };

    const handleClose = () => {
        if (wsService.current) {
            wsService.current.disconnect();
            wsService.current = null;
        }
        onClose();
    };

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Stop streaming indicator after message is complete
    useEffect(() => {
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage.sender === "ai") {
                // Set a small delay to show streaming is complete
                const timer = setTimeout(() => {
                    setIsStreaming(false);

                    // Remove message from streaming set
                    setStreamingMessageIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(lastMessage.id);

                        return newSet;
                    });
                }, 500);

                return () => clearTimeout(timer);
            }
        }
    }, [messages]);

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className={styles.backdrop} onClick={handleClose} />

            {/* Chat Panel */}
            <div className={styles.chatPanel}>
                <Row height="100%">
                    {/* Left Divider */}
                    <Box className={styles.leftDivider}>
                        <Divider size="small" />
                    </Box>

                    {/* Chat Content */}
                    <Box width="600px" height="100%" className={styles.chatSheetContent}>
                        <Column height="100%">
                            {/* Header */}
                            <Row alignmentHorizontal="justify" spacingInset="400 400 200 400">
                                <Row spacing="200" alignmentVertical="center">
                                    <Box className={styles.agentIcon}>
                                        <Icon tokens={sparklesSmallTokens} />
                                    </Box>
                                    <Text type="h300">Property Tax Agent</Text>
                                    <Tag>Experimental</Tag>
                                </Row>
                                <Row spacing="200">
                                    <Button
                                        type="secondary"
                                        size="small"
                                        onClick={() => setShowSkills(!showSkills)}
                                    >
                                        Explore my skills
                                    </Button>
                                    <Button type="icon" onClick={handleClose}>
                                        <Icon tokens={closeMediumTokens} />
                                    </Button>
                                </Row>
                            </Row>

                            {/* Connection Alert */}
                            {showConnectionAlert && !isConnected && (
                                <Box spacingInset="300 400" className={styles.connectionAlert}>
                                    <Row
                                        spacing="200"
                                        alignmentHorizontal="justify"
                                        alignmentVertical="center"
                                    >
                                        <Row spacing="200" alignmentVertical="center">
                                            <Text
                                                type="b300"
                                                className={styles.connectionAlertText}
                                            >
                                                ⚠️ Connection Broken
                                            </Text>
                                        </Row>
                                        <Row spacing="200">
                                            <Button
                                                type="primary"
                                                size="small"
                                                onClick={handleReconnect}
                                            >
                                                Reconnect
                                            </Button>
                                            <Button
                                                type="secondary"
                                                size="small"
                                                onClick={() => setShowConnectionAlert(false)}
                                            >
                                                Dismiss
                                            </Button>
                                        </Row>
                                    </Row>
                                </Box>
                            )}

                            {/* Chat Messages Area */}
                            <Box
                                spacingInset="0 400 0 400"
                                overflowY="auto"
                                className={styles.messagesArea}
                            >
                                {showSkills ? (
                                    /* Skills Panel */
                                    <Column spacing="300">
                                        <Row alignmentHorizontal="justify">
                                            <Text type="h400">Agent Capabilities</Text>
                                            <Button
                                                type="icon"
                                                size="small"
                                                onClick={() => setShowSkills(false)}
                                            >
                                                <Icon tokens={closeMediumTokens} />
                                            </Button>
                                        </Row>
                                        <Box className={styles.skillsContent}>
                                            <ReactMarkdown>{skillsContent}</ReactMarkdown>
                                        </Box>
                                    </Column>
                                ) : (
                                    /* Chat Messages */
                                    <Column spacing="300">
                                        {messages.map(message => (
                                            <Column key={message.id} spacing="200">
                                                {/* Show thinking section if available and toggle is enabled */}
                                                {message.sender === "ai" &&
                                                    message.thinking &&
                                                    showContext && (
                                                        <Row
                                                            spacing="200"
                                                            alignmentHorizontal="start"
                                                        >
                                                            <Box className={styles.messageIcon}>
                                                                <Icon
                                                                    tokens={sparklesSmallTokens}
                                                                />
                                                            </Box>
                                                            <ThinkingSection
                                                                thinking={message.thinking}
                                                                isStreaming={streamingMessageIds.has(
                                                                    message.id
                                                                )}
                                                            />
                                                        </Row>
                                                    )}

                                                {/* Main message content */}
                                                <Row
                                                    spacing="200"
                                                    alignmentHorizontal={
                                                        message.sender === "user" ? "end" : "start"
                                                    }
                                                >
                                                    {message.sender === "ai" && (
                                                        <Box className={styles.messageIcon}>
                                                            <Icon tokens={sparklesSmallTokens} />
                                                        </Box>
                                                    )}
                                                    <Box
                                                        className={
                                                            message.sender === "user"
                                                                ? styles.userMessage
                                                                : styles.aiMessage
                                                        }
                                                    >
                                                        {message.sender === "ai" ? (
                                                            <ReactMarkdown>
                                                                {message.text}
                                                            </ReactMarkdown>
                                                        ) : (
                                                            <Text type="b300">{message.text}</Text>
                                                        )}
                                                    </Box>
                                                    {message.sender === "user" && (
                                                        <Box className={styles.userMessageIcon}>
                                                            <Icon tokens={userTokens} />
                                                        </Box>
                                                    )}
                                                </Row>
                                            </Column>
                                        ))}
                                        {/* Streaming indicator */}
                                        {isStreaming && (
                                            <Row spacing="200" alignmentHorizontal="start">
                                                <Box className={styles.messageIcon}>
                                                    <Icon tokens={sparklesSmallTokens} />
                                                </Box>
                                                <Box className={styles.aiMessage}>
                                                    <Text
                                                        type="b300"
                                                        className={styles.typingIndicator}
                                                    >
                                                        ●●●
                                                    </Text>
                                                </Box>
                                            </Row>
                                        )}
                                        {/* Invisible div for auto-scroll */}
                                        <div ref={messagesEndRef} />
                                    </Column>
                                )}
                            </Box>

                            {/* Input Area */}
                            <Box className={styles.inputArea}>
                                <Column spacing="300">
                                    {/* Textarea */}
                                    <div
                                        ref={el => {
                                            if (el) {
                                                const textarea = el.querySelector("textarea");
                                                if (textarea) {
                                                    (textareaRef as any).current = textarea;
                                                }
                                            }
                                        }}
                                    >
                                        <Textarea
                                            value={inputValue}
                                            onChange={setInputValue}
                                            placeholder="Type your question..."
                                            rows={4}
                                        />
                                    </div>

                                    {/* Action Buttons Row */}
                                    <Row spacing="200" alignmentHorizontal="justify">
                                        {/* Left: Show Thinking Toggle */}
                                        <Row spacing="200" alignmentVertical="center">
                                            <Text type="b200">Show Thinking</Text>
                                            <Toggle
                                                checked={showContext}
                                                onChange={() => setShowContext(!showContext)}
                                            />
                                        </Row>

                                        {/* Right: Send Button */}
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={handleSendMessage}
                                            disabled={!inputValue.trim()}
                                        >
                                            Send
                                            <Icon tokens={sendSmallTokens} />
                                        </Button>
                                    </Row>

                                    {/* Disclaimer */}
                                    <Row
                                        spacing="100"
                                        alignmentVertical="center"
                                        spacingInset="200 0 0 0"
                                    >
                                        <Text type="b100" color="secondary">
                                            ⓘ Property Tax agent may not always be accurate. Verify
                                            response
                                        </Text>
                                    </Row>
                                </Column>
                            </Box>
                        </Column>
                    </Box>
                </Row>
            </div>
        </>
    );
};
